import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { formatDate, formatEuro, formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  Card,
  Dialog,
  Segmented,
  Table,
  TableSkeleton,
  useToast,
  type TableColumn,
} from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { usePermissions } from '../../../lib/session';
import { ApiError, errorMessage } from '../../../lib/api';
import { downloadCsv, markSharePaidOut, useInvalidateDeals, usePartnerCommissions } from '../api';
import { FieldBlock } from '../dialogs/bits';
import { DealsTabs, type DealsTab } from '../DealsTabs';
import { todayKey } from '../../tasks/dates';
import type { PartnerCommissionRow, PartnerShare } from '../types';
import pageStyles from '../DealsPage.module.css';
import dialogStyles from '../dialogs/dialogs.module.css';
import styles from '../invoicing/invoicing.module.css';

/**
 * Controlling 07 „Provisionen je Partner": the year switch, the partner table (Partner · Rolle · Deals ·
 * Verdient · Offen · Hinweis), the CSV export and — for admins — „Auszahlung erfassen" per share of a
 * partner (`POST /commissions/shares/:id/paid-out`, the footnote of the screen).
 */

export interface CommissionsTabProps {
  onTabChange: (tab: DealsTab) => void;
}

function PayoutDialog({
  open,
  partner,
  onClose,
  onDone,
}: {
  open: boolean;
  partner: PartnerCommissionRow | null;
  onClose: () => void;
  onDone: (share: PartnerShare) => void;
}) {
  const formId = useId();
  const [shareId, setShareId] = useState('');
  const [paidOutOn, setPaidOutOn] = useState(todayKey());
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const payable = useMemo(
    () => (partner?.shares ?? []).filter((share) => share.status === 'due' && share.paidOutAt === null),
    [partner],
  );

  useEffect(() => {
    if (!open) return;
    setShareId(payable[0]?.id ?? '');
    setPaidOutOn(todayKey());
    setBanner(null);
  }, [open, payable]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const share = payable.find((entry) => entry.id === shareId);
    if (!share || pending) return;
    setPending(true);
    setBanner(null);
    try {
      await markSharePaidOut(share.id, paidOutOn || null);
      onDone(share);
    } catch (error) {
      setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      tone="success"
      title="Auszahlung erfassen"
      subtitle={partner ? `${partner.name} · ${partner.roleLabel}` : ''}
      className={dialogStyles.stageDialog}
      initialFocus="container"
      footerNote="Ausgezahlt wird erst nach Provisionseingang (Status „fällig“)."
      footer={
        <>
          <Button variant="secondary" className={dialogStyles.footBtn} onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="dark"
            className={dialogStyles.footBtn}
            loading={pending}
            disabled={payable.length === 0}
          >
            Auszahlung erfassen
          </Button>
        </>
      }
    >
      <form id={formId} className={dialogStyles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {payable.length === 0 ? <Banner tone="neutral">Kein fälliger Anteil offen.</Banner> : null}
        {payable.length > 0 ? (
          <div className={styles.shares} role="radiogroup" aria-label="Anteil">
            {payable.map((share) => (
              <label
                key={share.id}
                className={clsx(dialogStyles.reason, share.id === shareId && dialogStyles.reasonActive)}
              >
                <input
                  type="radio"
                  name={`${formId}-share`}
                  className={dialogStyles.reasonInput}
                  value={share.id}
                  checked={share.id === shareId}
                  onChange={() => setShareId(share.id)}
                />
                <span className={dialogStyles.reasonDot} aria-hidden="true" />
                <span className={dialogStyles.reasonLabel}>{share.dealLabel}</span>
                <span className={styles.shareAmount}>{formatEuro(share.amountCents)}</span>
              </label>
            ))}
          </div>
        ) : null}
        <FieldBlock label="Ausgezahlt am" note="Datum" htmlFor={`${formId}-on`}>
          <input
            id={`${formId}-on`}
            type="date"
            className={dialogStyles.input}
            value={paidOutOn}
            onChange={(event) => setPaidOutOn(event.target.value)}
          />
        </FieldBlock>
      </form>
    </Dialog>
  );
}

export function CommissionsTab({ onTabChange }: CommissionsTabProps) {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const permissions = usePermissions();
  const { toast } = useToast();
  const invalidate = useInvalidateDeals();
  const [year, setYear] = useState<number | null>(null);
  const [payout, setPayout] = useState<PartnerCommissionRow | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const commissions = usePartnerCommissions(year, true);
  const admin = permissions.is('settings.organization', 'yes');
  const forbidden = commissions.error instanceof ApiError && commissions.error.status === 403;

  const columns = useMemo<TableColumn<PartnerCommissionRow>[]>(() => {
    const base: TableColumn<PartnerCommissionRow>[] = [
      {
        id: 'partner',
        header: 'Partner',
        track: 'minmax(0,1.2fr)',
        cell: (row) => <span className={clsx(styles.cell, styles.cellStrong)}>{row.name}</span>,
      },
      {
        id: 'role',
        header: 'Rolle',
        track: 'minmax(0,1.2fr)',
        cell: (row) => <span className={clsx(styles.cell, styles.cellMuted)}>{row.roleLabel}</span>,
      },
      {
        id: 'deals',
        header: 'Deals',
        track: '70px',
        numeric: true,
        cell: (row) => <span className={styles.numeric}>{formatInteger(row.deals)}</span>,
      },
      {
        id: 'earned',
        header: 'Verdient',
        track: '120px',
        numeric: true,
        cell: (row) => <span className={styles.numeric}>{formatEuro(row.earnedCents)}</span>,
      },
      {
        id: 'open',
        header: 'Offen',
        track: '120px',
        numeric: true,
        cell: (row) => <span className={styles.numeric}>{formatEuro(row.openCents)}</span>,
      },
      {
        id: 'note',
        header: 'Hinweis',
        track: 'minmax(0,1.3fr)',
        cell: (row) => <span className={clsx(styles.cell, styles.cellMuted)}>{row.note}</span>,
      },
    ];
    if (admin) {
      base.push({
        id: 'actions',
        header: '',
        track: '170px',
        align: 'end',
        cell: (row) => (
          <span className={styles.rowActions} onClick={(event) => event.stopPropagation()}>
            <Button
              size="small"
              variant="secondary"
              disabled={!row.shares.some((share) => share.status === 'due' && share.paidOutAt === null)}
              onClick={() => setPayout(row)}
            >
              Auszahlung erfassen
            </Button>
          </span>
        ),
      });
    }
    return base;
  }, [admin]);

  const data = commissions.data;
  const years = data?.years ?? [];
  const currentYear = data?.year ?? year ?? new Date().getFullYear();
  const yearItems = (years.length > 0 ? years : [currentYear]).map((value) => ({
    value: String(value),
    label: String(value),
  }));
  const expandedRow = data?.items.find((row) => row.userId === expanded) ?? null;

  return (
    <div className={pageStyles.page} style={{ padding: mobile ? undefined : '24px 32px 32px', gap: 18 }}>
      <div className={pageStyles.listHead}>
        <div className={pageStyles.listHeadText}>
          <div className={pageStyles.crumb}>Deals</div>
          <h1 className={pageStyles.listTitle}>Provisionen</h1>
          <div className={pageStyles.listCount}>{currentYear} · Immobilien und Finanzierungen</div>
        </div>
        <div className={pageStyles.headRight}>
          <Segmented
            label="Jahr"
            value={String(currentYear)}
            items={yearItems}
            onChange={(value) => setYear(Number(value))}
          />
          {data && !forbidden ? (
            <Button
              variant="secondary"
              onClick={() => downloadCsv(`/deals/commissions/export?year=${currentYear}`)}
            >
              Export
            </Button>
          ) : null}
        </div>
      </div>
      <DealsTabs value="commissions" onChange={onTabChange} />

      {commissions.isPending ? (
        <Card flush className={styles.tableCard}>
          <TableSkeleton
            columns={['minmax(0,1.2fr)', 'minmax(0,1.2fr)', '70px', '120px', '120px', 'minmax(0,1.3fr)']}
            rows={4}
            headerTone="plain"
          />
        </Card>
      ) : null}
      {commissions.isError ? (
        forbidden ? (
          <Banner tone="info">{commissions.error.message}</Banner>
        ) : (
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void commissions.refetch()}>
                Erneut laden
              </Button>
            }
          >
            {errorMessage(commissions.error)}
          </Banner>
        )
      ) : null}
      {data ? (
        <>
          <Card flush className={styles.tableCard}>
            <Table
              label="Provisionen"
              columns={columns}
              rows={data.items}
              rowKey={(row) => row.userId}
              headerTone="plain"
              onRowClick={(row) => setExpanded((current) => (current === row.userId ? null : row.userId))}
              emptyText={`Keine Provisionen in ${currentYear}.`}
            />
          </Card>
          {expandedRow ? (
            <Card style={{ padding: '16px 20px' }} aria-label={`Anteile ${expandedRow.name}`}>
              <div className={styles.emptyEyebrow}>Anteile · {expandedRow.name}</div>
              <div className={styles.shares} style={{ marginTop: 10 }}>
                {expandedRow.shares.map((share) => (
                  <button
                    key={share.id}
                    type="button"
                    className={styles.shareRow}
                    style={{
                      background: 'transparent',
                      font: 'inherit',
                      textAlign: 'left',
                      width: '100%',
                      cursor: 'pointer',
                    }}
                    onClick={() => void navigate(`/deals/${share.dealId}?tab=commission`)}
                  >
                    <span className={styles.shareLabel}>{share.dealLabel}</span>
                    <span className={styles.shareMeta}>
                      {share.status === 'due' ? 'fällig' : 'eingefroren'}
                      {share.capped ? ' · gedeckelt' : ''}
                      {share.paidOutAt ? ` · ausgezahlt ${formatDate(share.paidOutAt)}` : ''}
                    </span>
                    <span className={styles.shareAmount}>{formatEuro(share.amountCents)}</span>
                  </button>
                ))}
              </div>
            </Card>
          ) : null}
          <div className={styles.footnote}>{data.footnote}</div>
        </>
      ) : null}

      <PayoutDialog
        open={payout !== null}
        partner={payout}
        onClose={() => setPayout(null)}
        onDone={(share) => {
          setPayout(null);
          void invalidate();
          toast({ message: `Auszahlung erfasst: ${share.dealLabel} · ${formatEuro(share.amountCents)}.` });
        }}
      />
    </div>
  );
}
